// src/lib/earnings.js
// Commission / payout math for HomeIQa contract agents.
//
// Default split: 25% to HomeIQa, 75% to the contract agent.
// Per-lead override lives at lead.commissionOverrideToHomeIQa
// (a number 0..1 like 0.20 for 20%); per-agent default lives at
// agents/{uid}.defaultCommissionToHomeIQa.
//
// "Earnings-counting deals" are leads with status 'Assigned' or 'Closed'
// and a positive assignment fee on lead.calc.assignmentFee.

export const DEFAULT_HOMEIQA_PCT = 0.25;

export function getCommissionPct(lead, agentDefault) {
  const override = lead?.commissionOverrideToHomeIQa;
  if (typeof override === 'number' && override >= 0 && override <= 1) return override;
  if (typeof agentDefault === 'number' && agentDefault >= 0 && agentDefault <= 1) return agentDefault;
  return DEFAULT_HOMEIQA_PCT;
}

// Split one lead's assignment fee into HomeIQa cut + agent cut.
// Returns { fee, homeIQaCut, agentCut, pctToHomeIQa, pctToAgent }.
export function splitDeal(lead, agentDefaultPct) {
  const fee = Number(lead?.calc?.assignmentFee) || 0;
  const pct = getCommissionPct(lead, agentDefaultPct);
  return {
    fee,
    pctToHomeIQa: pct,
    pctToAgent: 1 - pct,
    homeIQaCut: Math.round(fee * pct),
    agentCut: Math.round(fee * (1 - pct))
  };
}

export function isEarningsDeal(lead) {
  const status = (lead?.status || '').toLowerCase();
  if (status !== 'assigned' && status !== 'closed') return false;
  return Number(lead?.calc?.assignmentFee) > 0;
}

// Aggregate earnings across a set of leads + per-agent defaults.
// Returns { totalFees, totalHomeIQa, totalAgent, dealCount, projectedFees,
//          projectedAgentCut, projectedHomeIQa } — projected = active deals
// (status: Negotiating / Under Contract) that haven't closed yet.
export function aggregateEarnings(leads, agentDefaultPct) {
  let closed = { fees: 0, homeIQa: 0, agent: 0, count: 0 };
  let projected = { fees: 0, homeIQa: 0, agent: 0, count: 0 };
  for (const lead of leads || []) {
    const fee = Number(lead?.calc?.assignmentFee) || 0;
    if (!fee) continue;
    const status = (lead.status || '').toLowerCase();
    const split = splitDeal(lead, agentDefaultPct);
    if (status === 'assigned' || status === 'closed') {
      closed.fees += split.fee;
      closed.homeIQa += split.homeIQaCut;
      closed.agent += split.agentCut;
      closed.count += 1;
    } else if (status === 'negotiating' || status === 'under contract') {
      projected.fees += split.fee;
      projected.homeIQa += split.homeIQaCut;
      projected.agent += split.agentCut;
      projected.count += 1;
    }
  }
  return {
    totalFees: closed.fees,
    totalHomeIQa: closed.homeIQa,
    totalAgent: closed.agent,
    dealCount: closed.count,
    projectedFees: projected.fees,
    projectedAgentCut: projected.agent,
    projectedHomeIQa: projected.homeIQa,
    projectedCount: projected.count
  };
}
